import { useEffect, useRef, lazy, Suspense } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Map, Building2, ArrowRightLeft } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { useUIStore } from '@/stores/uiStore'
import type { TabValue } from '@/stores/uiStore'
import { VI_MESSAGES } from '@/constants/vietnamese'
import { SkeletonLoader } from '@/components/common/SkeletonLoader'

// Lazy load all tabs for better initial performance
const WardListTab = lazy(() =>
  import('@/components/wards/WardListTab').then((module) => ({ default: module.WardListTab }))
)
const OfficesTab = lazy(() =>
  import('@/components/offices/OfficesTab').then((module) => ({ default: module.OfficesTab }))
)
const AddressConverterTab = lazy(() =>
  import('@/components/address-converter/AddressConverterTab').then((module) => ({
    default: module.AddressConverterTab,
  }))
)

export function LeftPanel() {
  const { isPanelOpen, activeTab, togglePanel, setActiveTab, panelWidth, isMobile } = useUIStore()
  const prevIsMobileRef = useRef(isMobile)

  // Handle viewport transitions (desktop → mobile)
  useEffect(() => {
    // Only close panel when transitioning FROM desktop TO mobile
    if (!prevIsMobileRef.current && isMobile && isPanelOpen) {
      togglePanel()
    }

    // Update the ref with current value for next render
    prevIsMobileRef.current = isMobile
  }, [isMobile, isPanelOpen, togglePanel])

  // Initial setup - close panel on first mount if mobile
  // useEffect(() => {
  //   if (isMobile && isPanelOpen) {
  //     togglePanel();
  //   }
  // }, []); // Only run once on mount

  return (
    <AnimatePresence mode="wait">
      {isPanelOpen && (
        <motion.aside
          initial={{ x: -panelWidth }}
          animate={{ x: 0 }}
          exit={{ x: -panelWidth }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative z-10 h-full bg-white shadow-xl"
          style={{ width: panelWidth }}
        >
          {/* App Title and Toggle */}
          <div className="bg-blue-600 text-white">
            <div className="flex items-start justify-between px-4 py-3">
              <div className="flex flex-1 items-center gap-3">
                <img
                  src={`/${import.meta.env.VITE_STATE_CODE}.png`}
                  alt="Logo"
                  className="h-10 w-10 flex-shrink-0 object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold">{VI_MESSAGES.APP.TITLE}</h1>
                  <p className="text-xs opacity-90">{VI_MESSAGES.APP.SUBTITLE}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePanel}
                className="h-8 w-8 text-white hover:bg-blue-700 hover:text-white"
                aria-label={VI_MESSAGES.PANEL.TOGGLE}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TabValue)}
            className="flex h-[calc(100%-5rem)] flex-col"
          >
            <TabsList className="grid h-12 w-full grid-cols-3 rounded-none bg-gray-50">
              <TabsTrigger value="offices" className="tab-trigger">
                <Building2 className="h-4 w-4" />
                <span className="hidden sm:inline">{VI_MESSAGES.TABS.OFFICES}</span>
              </TabsTrigger>
              <TabsTrigger value="wards" className="tab-trigger">
                <Map className="h-4 w-4" />
                <span className="hidden sm:inline">{VI_MESSAGES.TABS.WARDS}</span>
              </TabsTrigger>
              <TabsTrigger value="address-converter" className="tab-trigger">
                <ArrowRightLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{VI_MESSAGES.TABS.ADDRESS_CONVERTER}</span>
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-hidden">
              <TabsContent value="offices" className="m-0 h-full">
                <Suspense fallback={<SkeletonLoader count={3} />}>
                  <OfficesTab />
                </Suspense>
              </TabsContent>
              <TabsContent value="wards" className="m-0 h-full">
                <Suspense fallback={<SkeletonLoader count={3} />}>
                  <WardListTab />
                </Suspense>
              </TabsContent>
              <TabsContent value="address-converter" className="m-0 h-full">
                <Suspense fallback={<SkeletonLoader count={3} />}>
                  <AddressConverterTab />
                </Suspense>
              </TabsContent>
            </div>
          </Tabs>
        </motion.aside>
      )}

      {/* Collapsed State - Toggle Button */}
      {!isPanelOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute top-4 left-0 z-10"
        >
          <Button
            variant="default"
            size="sm"
            onClick={togglePanel}
            className="rounded-l-none border border-blue-600 bg-blue-600 text-white shadow-lg hover:bg-blue-700"
            aria-label={VI_MESSAGES.PANEL.TOGGLE}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
